import { motion } from 'framer-motion'
import {
  Calendar, Clock, ArrowRight, Tag, Search, TrendingUp, Star
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { blogApi, type BlogPost } from '../lib/api'
import { useLocale } from '../i18n/Link'

// `query` is the category value the blog API filters on (backend data, not UI text);
// the visible label comes from blog.json (categories.<id>).
const categories = [
  { id: 'all', query: undefined, count: 12 },
  { id: 'product-updates', query: 'Ürün Güncellemeleri', count: 5 },
  { id: 'security', query: 'Güvenlik', count: 3 },
  { id: 'tips', query: 'İpuçları', count: 4 },
]

function formatDate(dateStr: string, locale: string) {
  return new Date(dateStr).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function Blog() {
  const { t } = useTranslation(['blog', 'common'])
  const { htmlLang } = useLocale()
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const categoryQuery = categories.find((c) => c.id === activeCategory)?.query
  const { data: blogData, loading } = useApi(
    () => blogApi.getAll({ category: categoryQuery, search: searchQuery || undefined }),
    [activeCategory, searchQuery]
  )
  const { data: featuredPost } = useApi(() => blogApi.getFeatured(), [])
  const filteredPosts: BlogPost[] = blogData?.posts || []

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      
      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/blog"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.blog'), url: '/blog' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <TrendingUp className="w-4 h-4" />
            {t('eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('heroTitle')}{' '}
            <span className="text-gradient">{t('heroTitleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            {t('heroText')}
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 glass rounded-xl text-white placeholder-gray-500 focus:outline-hidden focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2 rounded-full font-medium transition-all duration-200 ${
                  activeCategory === category.id
                    ? 'bg-primary text-white'
                    : 'glass text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {t(`categories.${category.id}`)}
                <span className={`ml-2 text-xs ${activeCategory === category.id ? 'text-white/70' : 'text-gray-500'}`}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">{t('loading')}</p>
          </div>
        )}

        {/* Featured Post */}
        {!loading && !searchQuery && activeCategory === 'all' && featuredPost && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="glass rounded-3xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto bg-gradient-to-br from-primary/30 via-secondary/30 to-primary/30 flex items-center justify-center">
                  <Star className="w-24 h-24 text-white/50" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">
                      {featuredPost.category}
                    </span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.createdAt, htmlLang)}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {t('readTime', { n: featuredPost.readTime })}
                    </span>
                    <button className="flex items-center gap-2 text-primary hover:text-primary-dark font-medium transition-colors">
                      {t('readMore')}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Posts Grid */}
        {!loading && <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl overflow-hidden group cursor-pointer"
            >
              <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-secondary/30 transition-all">
                <Tag className="w-12 h-12 text-white/30" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                    {post.category}
                  </span>
                  <span className="text-gray-500 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('readTimeShort', { n: post.readTime })}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                      {post.author.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span className="text-gray-500 text-sm">{post.author.name}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{formatDate(post.createdAt, htmlLang)}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>}

        {/* Empty State */}
        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('empty.title')}</h3>
            <p className="text-gray-400">{t('empty.text')}</p>
          </div>
        )}

      </div>
    </motion.div>
  )
}
